"use client";

import type { ReactNode } from "react";
import { DropdownMenue, MENUE_EINTRAG } from "../../dropdown-menue";
import { SymInfo, SymKalender, SymSeite } from "../lesen/lese-symbole";
import { SymAt, SymBildEinfuegen, SymCodeBlock, SymPlus, SymRunter, SymTabelle, SymVerzeichnis } from "./bearbeiten-symbole";
import type { EditorAnfrageArt, EditorAnker } from "./dialoge";

/**
 * Befehle des Menüs „Einfügen“; gleiche Form wie `HornEinfuegeBefehle` aus `@horn/editor` (`useHornEinfuegen`). Dieses Paket hängt
 * bewusst nicht von `@horn/editor` oder TipTap ab, die Form genügt (strukturelle Typen, der Typecheck der Verbraucher belegt es).
 */
export interface EditorEinfuegeBefehle {
  bearbeitbar: boolean;
  hinweisfeld: (ton: "info" | "ok" | "warn" | "risk") => void;
  datum: () => void;
  unterseiten: () => void;
  codeblock: () => void;
  /** Fügt eine Tabelle mit Kopfzeile ein; ohne Dialoge der Seite (`anfrage`) setzt das Menü die Vorgabe 3 × 3. */
  tabelle: (zeilen: number, spalten: number) => unknown;
  /** Nur mit Personensuche der Seite. */
  person?: () => void;
  /** Nur mit Dialogen der Seite (Gitterwahl, Bildablage, Seitenwahl). */
  anfrage?: (art: EditorAnfrageArt, anker?: EditorAnker | null) => void;
}

// Auslöser wie die Sekundär-Kette des `Knopf` (Größe sm); die Menü-Fläche setzt `DropdownMenue`. Dokumentierte Ausnahme der
// Sperrklinke R15 (knopf-ausnahmen.json, Anker EINFUEGEN_AUSLOESER), wie die übrigen Menü-Auslöser.
const EINFUEGEN_AUSLOESER =
  "py-1.5 px-3 inline-flex items-center gap-x-1.5 text-sm font-medium rounded-lg border border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus";

function Eintrag({ symbol, text, onClick }: { symbol: ReactNode; text: string; onClick: () => void }) {
  return (
    <button type="button" role="menuitem" className={MENUE_EINTRAG} onClick={onClick}>
      {symbol}
      {text}
    </button>
  );
}

/**
 * Menü „Einfügen“ der Leiste (G-W4 Variante B, Betreiber 13.10.2026, Abweichung AW-064 (6)): die schlanke Leiste bleibt wie bei den Besprechungen, die
 * seltenen Bausteine liegen im Menü. Einträge: Tabelle …, Bild …, Hinweisfeld, Datum, Seite verlinken …, Person erwähnen …,
 * Unterseiten-Verzeichnis, Code-Block; „…“ = es folgt ein Dialog. Einträge, für die die Seite keinen Weg anbindet (Dialoge,
 * Personensuche), erscheinen nicht. Der Baustein prüft keine Rechte und fügt nichts selbst ein: er ruft die Befehle (`useHornEinfuegen`).
 */
export function EditorEinfuegenMenue({ befehle }: { befehle: EditorEinfuegeBefehle }) {
  if (!befehle.bearbeitbar) return null;
  const anfrage = befehle.anfrage;
  return (
    <DropdownMenue
      ausloeserKlasse={EINFUEGEN_AUSLOESER}
      ausloeserLabel="Einfügen"
      rechts={false}
      ausloeser={
        <>
          <SymPlus />
          Einfügen
          <SymRunter />
        </>
      }
    >
      {({ schliessen }) => {
        const tue = (aktion: () => void) => () => {
          schliessen();
          aktion();
        };
        return (
          <>
            {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-editor-and-toolbar-menus/formatting-action-menu */}
            <div className="w-60 p-1 space-y-0.5">
              <Eintrag symbol={<SymTabelle />} text="Tabelle …" onClick={tue(() => (anfrage ? anfrage("tabelle") : void befehle.tabelle(3, 3)))} />
              {anfrage ? <Eintrag symbol={<SymBildEinfuegen />} text="Bild …" onClick={tue(() => anfrage("bild"))} /> : null}
              <Eintrag symbol={<SymInfo />} text="Hinweisfeld" onClick={tue(() => befehle.hinweisfeld("info"))} />
              <Eintrag symbol={<SymKalender />} text="Datum" onClick={tue(befehle.datum)} />
              {anfrage ? <Eintrag symbol={<SymSeite />} text="Seite verlinken …" onClick={tue(() => anfrage("seite"))} /> : null}
              {befehle.person ? <Eintrag symbol={<SymAt />} text="Person erwähnen …" onClick={tue(befehle.person)} /> : null}
              <Eintrag symbol={<SymVerzeichnis />} text="Unterseiten-Verzeichnis" onClick={tue(befehle.unterseiten)} />
              <Eintrag symbol={<SymCodeBlock />} text="Code-Block" onClick={tue(befehle.codeblock)} />
            </div>
          </>
        );
      }}
    </DropdownMenue>
  );
}
